'use client';

import { useEffect, useMemo } from 'react';
import {
  buildConnectorScanNetworkOptions,
  INTERNAL_NETWORK_TRANSPORTS,
  InternalNetworkSelectField,
  type ConnectorNetworkPickerRenderProps,
  type InternalNetworkOption,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import type { InternalAccessNetworkDto } from '../../../../hooks/api/useInternalAccessNetworks';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';

type DefaultableNetwork = { id: string; isDefault: boolean };

type ExternalConnectorNetworkPickerProps = ConnectorNetworkPickerRenderProps & {
  internalAccessNetworks: readonly InternalAccessNetworkDto[];
  isInternalAccessNetworksLoading: boolean;
  disabled: boolean;
};

const toInternalAccessNetworkId = (
  option: InternalNetworkOption | undefined,
): string | null =>
  option?.transport === INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS
    ? option.id
    : null;

const findDefaultOption = ({
  options,
  internalAccessNetworks,
}: {
  options: readonly InternalNetworkOption[];
  internalAccessNetworks: readonly DefaultableNetwork[];
}): InternalNetworkOption | undefined =>
  options.find((option) =>
    internalAccessNetworks.some(
      (network) => network.id === option.id && network.isDefault,
    ),
  ) ?? options[0];

export function ExternalConnectorNetworkPicker({
  internalAccessNetworkId,
  onInternalAccessNetworkIdChange,
  internalAccessNetworks,
  isInternalAccessNetworksLoading,
  disabled,
}: ExternalConnectorNetworkPickerProps) {
  const t = useTranslations(
    'designSystem.applicationSettings.networking.internalAccess',
  );
  const {
    isFeatureFlagEnabled: isInternalAccessEnabled,
    isFetched: areFeatureFlagsResolved,
  } = useFeatureFlags('TAILSCALE_INTERNAL_APP_ENABLED');
  const options = useMemo(
    () =>
      buildConnectorScanNetworkOptions({
        internalAccessNetworks,
        selectedNetworkId: internalAccessNetworkId,
        isInternalAccessEnabled,
        networkFallbackLabel: t('network'),
      }),
    [
      internalAccessNetworkId,
      internalAccessNetworks,
      isInternalAccessEnabled,
      t,
    ],
  );
  const selectedOption = options.find(
    (option) =>
      isInternalAccessEnabled &&
      option.transport === INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS &&
      option.id === internalAccessNetworkId,
  );
  const defaultNetworkId = toInternalAccessNetworkId(
    findDefaultOption({ options, internalAccessNetworks }),
  );
  const isLoading =
    !areFeatureFlagsResolved ||
    (isInternalAccessEnabled && isInternalAccessNetworksLoading);
  const resolvedNetworkId = isInternalAccessEnabled
    ? (internalAccessNetworkId ?? defaultNetworkId)
    : null;
  const isAwaitingOptions = isInternalAccessEnabled && options.length === 0;

  useEffect(() => {
    if (
      !areFeatureFlagsResolved ||
      isAwaitingOptions ||
      resolvedNetworkId === internalAccessNetworkId
    ) {
      return;
    }

    onInternalAccessNetworkIdChange(resolvedNetworkId);
  }, [
    areFeatureFlagsResolved,
    internalAccessNetworkId,
    isAwaitingOptions,
    onInternalAccessNetworkIdChange,
    resolvedNetworkId,
  ]);

  return (
    <InternalNetworkSelectField
      items={options}
      value={selectedOption?.value ?? ''}
      onValueChange={(value) =>
        onInternalAccessNetworkIdChange(
          toInternalAccessNetworkId(
            options.find((option) => option.value === value),
          ),
        )
      }
      isLoading={isLoading}
      disabled={disabled}
      labels={{
        networkLabel: t('network'),
        networkHelper: t('description'),
        networkPlaceholder: t('selectNetwork'),
        noNetworksAvailable: t('noNetworksAvailable'),
        formatSingleNetwork: (name: string) => name,
      }}
    />
  );
}
